
import { ADTScenario } from '@/types/adt';

export interface PortfolioProjection {
  year: number;
  age: number;
  portfolioValue: number;
  platformFeesPaid: number;
  netReturn: number;
  grossReturn: number;
}

export class PortfolioSimulator {
  /**
   * Calculate annual platform fees based on scenario strategy with min/max constraints
   */
  static calculatePlatformFees(portfolioValue: number, scenario: ADTScenario): number {
    const strategy = scenario.strategy;
    if (!strategy) return 0;

    // Calculate base fee
    const percentageFee = (strategy.platformCostPercentage || 0) / 100;
    const fixedFee = strategy.platformCostFixed || 0;
    let totalFee = (portfolioValue * percentageFee) + fixedFee;

    // Apply minimum constraints
    if (strategy.platformFeeMinAmount && totalFee < strategy.platformFeeMinAmount) {
      totalFee = strategy.platformFeeMinAmount;
    }
    if (strategy.platformFeeMinPercentage && portfolioValue > 0) {
      const minFromPercentage = portfolioValue * (strategy.platformFeeMinPercentage / 100);
      totalFee = Math.max(totalFee, minFromPercentage);
    }

    // Apply maximum constraints
    if (strategy.platformFeeMaxAmount && totalFee > strategy.platformFeeMaxAmount) {
      totalFee = strategy.platformFeeMaxAmount;
    }
    if (strategy.platformFeeMaxPercentage && portfolioValue > 0) {
      const maxFromPercentage = portfolioValue * (strategy.platformFeeMaxPercentage / 100);
      totalFee = Math.min(totalFee, maxFromPercentage);
    }

    return Math.max(0, totalFee);
  }
  
  /**
   * Apply platform fees to portfolio returns
   */
  static applyPlatformFees(
    grossReturn: number, 
    portfolioValue: number, 
    scenario: ADTScenario
  ): { netReturn: number; feesPaid: number } {
    const feesPaid = this.calculatePlatformFees(portfolioValue, scenario);
    const netReturn = grossReturn - feesPaid;
    
    return { netReturn, feesPaid };
  }
  
  /**
   * Project portfolio value with fees over time
   */
  static projectWithFees(
    initialValue: number,
    annualReturn: number,
    scenario: ADTScenario,
    years: number
  ): PortfolioProjection[] {
    const projections: PortfolioProjection[] = [];
    let currentValue = initialValue;
    const startAge = scenario.basicInformation?.currentAge || 30;
    
    for (let year = 0; year <= years; year++) {
      const grossReturn = currentValue * (annualReturn / 100);
      const { netReturn, feesPaid } = this.applyPlatformFees(grossReturn, currentValue, scenario);
      
      projections.push({
        year,
        age: startAge + year,
        portfolioValue: currentValue,
        platformFeesPaid: feesPaid,
        netReturn,
        grossReturn
      });
      
      // Update portfolio value for next year
      currentValue = currentValue + netReturn;
      
      // Apply withdrawals if in retirement
      const retirementAge = scenario.basicInformation?.retirementAge || 65;
      if (startAge + year >= retirementAge) {
        const withdrawalRate = (scenario.strategy?.withdrawalRate || 4) / 100;
        const withdrawal = currentValue * withdrawalRate;
        currentValue = Math.max(0, currentValue - withdrawal);
      }
    }
    
    return projections;
  }
}
